'use client';
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { getMarketSession, isAfterMarketClose, type MarketSession } from '@/lib/india-market';
import { markTickReceived } from '@/lib/tick-latency';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TickData {
  symbol: string;
  ltp: number;
  change: number;
  pct: number;
  open: number;
  high: number;
  low: number;
  prevClose: number;
  volume: number;
  bid: number;
  ask: number;
  timestamp: number;
  // Extended live fields from Upstox full feed
  oi?: number;           // open interest
  iv?: number;           // implied volatility (raw, e.g. 0.1685)
  atp?: number;          // average traded price
  tbq?: number;          // total buy quantity
  tsq?: number;          // total sell quantity
  greeks?: {
    delta?: number;
    theta?: number;
    gamma?: number;
    vega?: number;
    rho?: number;
  } | null;
  marketDepth?: Array<{
    bidQ: number;
    bidP: number;
    askQ: number;
    askP: number;
  }> | null;
}

export interface IndexData {
  symbol: string;
  value: number;
  change: number;
  pct: number;
  open: number;
  high: number;
  low: number;
  prevClose: number;
}

interface MarketDataContextValue {
  ticks: Record<string, TickData>;
  indices: Record<string, IndexData>;
  isConnected: boolean;
  isLive: boolean;
  marketSession: MarketSession;
  subscribe: (symbols: string[]) => void;
  unsubscribe: (symbols: string[]) => void;
  /** performance.now() timestamp when each symbol's last tick was received */
  tickReceivedAt: Record<string, number>;
}

// ─── Seed Data (base prices for simulation / initial state) ──────────────────

const BASE_STOCKS: Record<string, Omit<TickData, 'timestamp' | 'bid' | 'ask'>> = {
  RELIANCE:   { symbol: 'RELIANCE',   ltp: 2987.45, change: -12.30, pct: -0.41, open: 2998.00, high: 3012.00, low: 2971.20, prevClose: 2999.75, volume: 2300000 },
  TCS:        { symbol: 'TCS',        ltp: 4123.80, change:  45.60, pct:  1.12, open: 4078.20, high: 4135.00, low: 4067.00, prevClose: 4078.20, volume: 1100000 },
  HDFCBANK:   { symbol: 'HDFCBANK',   ltp: 1876.25, change:  -8.75, pct: -0.46, open: 1884.00, high: 1892.00, low: 1868.50, prevClose: 1885.00, volume: 3800000 },
  INFY:       { symbol: 'INFY',       ltp: 1654.90, change:  22.10, pct:  1.35, open: 1632.80, high: 1662.00, low: 1621.00, prevClose: 1632.80, volume: 2700000 },
  ICICIBANK:  { symbol: 'ICICIBANK',  ltp: 1243.60, change:  15.40, pct:  1.25, open: 1228.20, high: 1251.00, low: 1224.00, prevClose: 1228.20, volume: 4200000 },
  BAJFINANCE: { symbol: 'BAJFINANCE', ltp: 7234.80, change: 112.50, pct:  1.58, open: 7122.30, high: 7248.00, low: 7101.00, prevClose: 7122.30, volume:  900000 },
  WIPRO:      { symbol: 'WIPRO',      ltp:  567.35, change:  -3.20, pct: -0.56, open:  570.55, high:  574.00, low:  562.10, prevClose:  570.55, volume: 5100000 },
  AXISBANK:   { symbol: 'AXISBANK',   ltp: 1187.40, change:  18.90, pct:  1.62, open: 1168.50, high: 1194.00, low: 1163.00, prevClose: 1168.50, volume: 2900000 },
  SUNPHARMA:  { symbol: 'SUNPHARMA',  ltp: 1876.60, change: -24.30, pct: -1.28, open: 1900.90, high: 1905.00, low: 1869.00, prevClose: 1900.90, volume: 1400000 },
  MARUTI:     { symbol: 'MARUTI',     ltp: 12456.00, change: 187.50, pct: 1.53, open: 12268.50, high: 12498.00, low: 12234.00, prevClose: 12268.50, volume:  400000 },
  TATASTEEL:  { symbol: 'TATASTEEL',  ltp:  167.85, change:  -2.45, pct: -1.44, open:  170.30, high:  172.00, low:  166.20, prevClose:  170.30, volume: 18700000 },
  ONGC:       { symbol: 'ONGC',       ltp:  287.60, change:   4.20, pct:  1.48, open:  283.40, high:  289.00, low:  281.50, prevClose:  283.40, volume: 8300000 },
};

const BASE_INDICES: Record<string, IndexData> = {
  'NIFTY 50':    { symbol: 'NIFTY 50',    value: 23654.70, change:  -88.65, pct: -0.37, open: 23743.35, high: 23780.00, low: 23610.00, prevClose: 23743.35 },
  BANKNIFTY:     { symbol: 'BANKNIFTY',   value: 53439.40, change:  216.00, pct:  0.41, open: 53223.40, high: 53510.00, low: 53150.00, prevClose: 53223.40 },
  FINNIFTY:      { symbol: 'FINNIFTY',    value: 25237.20, change:   98.65, pct:  0.39, open: 25138.55, high: 25290.00, low: 25100.00, prevClose: 25138.55 },
  GIFTNIFTY:     { symbol: 'GIFTNIFTY',   value: 23491.50, change:  -93.00, pct: -0.39, open: 23584.50, high: 23620.00, low: 23470.00, prevClose: 23584.50 },
  MIDCPNIFTY:    { symbol: 'MIDCPNIFTY',  value: 14351.90, change:  107.20, pct:  0.75, open: 14244.70, high: 14380.00, low: 14220.00, prevClose: 14244.70 },
  SENSEX:        { symbol: 'SENSEX',      value: 75183.36, change: -312.09, pct: -0.41, open: 75495.45, high: 75620.00, low: 75050.00, prevClose: 75495.45 },
  NIFTYMIDCAP150:{ symbol: 'NIFTYMIDCAP150', value: 22479.15, change: 173.20, pct: 0.78, open: 22305.95, high: 22510.00, low: 22280.00, prevClose: 22305.95 },
};

const INDEX_SYMBOLS = new Set(['NIFTY 50', 'BANKNIFTY', 'FINNIFTY', 'GIFTNIFTY', 'MIDCPNIFTY', 'SENSEX', 'NIFTYMIDCAP150']);

// ─── Stable seed (no Math.random / Date.now — safe for SSR) ──────────────────

function stableTickSeed(): Record<string, TickData> {
  const out: Record<string, TickData> = {};
  for (const [sym, base] of Object.entries(BASE_STOCKS)) {
    const spread = base.ltp * 0.0002;
    out[sym] = {
      ...base,
      bid: parseFloat((base.ltp - spread).toFixed(2)),
      ask: parseFloat((base.ltp + spread).toFixed(2)),
      timestamp: 0, // stable — will be updated on first client-side tick
    };
  }
  return out;
}

// ─── Simulation helpers ───────────────────────────────────────────────────────

function jitter(base: number, bps: number): number {
  const delta = base * (bps / 10000) * (Math.random() * 2 - 1);
  return parseFloat((base + delta).toFixed(2));
}

function simulateTick(prev: TickData): TickData {
  const ltp = jitter(prev.ltp, 8);
  const high = Math.max(prev.high, ltp);
  const low  = Math.min(prev.low,  ltp);
  const change = parseFloat((ltp - prev.prevClose).toFixed(2));
  const pct    = parseFloat(((change / prev.prevClose) * 100).toFixed(2));
  const spread = ltp * 0.0002;
  return {
    ...prev,
    ltp,
    change,
    pct,
    high,
    low,
    bid: parseFloat((ltp - spread).toFixed(2)),
    ask: parseFloat((ltp + spread).toFixed(2)),
    volume: prev.volume + Math.floor(Math.random() * 500),
    timestamp: Date.now(),
  };
}

function simulateIndex(prev: IndexData): IndexData {
  const value  = jitter(prev.value, 5);
  const high   = Math.max(prev.high, value);
  const low    = Math.min(prev.low,  value);
  const change = parseFloat((value - prev.prevClose).toFixed(2));
  const pct    = parseFloat(((change / prev.prevClose) * 100).toFixed(2));
  return { ...prev, value, change, pct, high, low };
}

// ─── Map Supabase market_live_data row → TickData / IndexData ─────────────────

interface MarketLiveRow {
  ticker: string;
  company_name: string;
  current_price: number;
  previous_close: number;
  change_amount: number;
  change_percent: number;
  open_price: number;
  high_price: number;
  low_price: number;
  volume: number;
  is_index: boolean;
  bid?: number;
  ask?: number;
  updated_at: string;
  // Extended live fields from Upstox full feed
  oi?: number | null;
  iv?: number | null;
  atp?: number | null;
  tbq?: number | null;
  tsq?: number | null;
  // Stored as JSON strings in Supabase (text columns)
  greeks?: string | { delta?: number; theta?: number; gamma?: number; vega?: number; rho?: number } | null;
  market_depth?: string | Array<{ bidQ: number; bidP: number; askQ: number; askP: number }> | null;
}

function parseJsonField<T>(val: string | T | null | undefined): T | null {
  if (val == null) return null;
  if (typeof val === 'string') {
    try { return JSON.parse(val) as T; } catch { return null; }
  }
  return val as T;
}

function rowToTick(row: MarketLiveRow): TickData {
  const ltp = Number(row.current_price);
  const spread = ltp * 0.0002;
  return {
    symbol: row.ticker,
    ltp,
    change: Number(row.change_amount),
    pct: Number(row.change_percent),
    open: Number(row.open_price),
    high: Number(row.high_price),
    low: Number(row.low_price),
    prevClose: Number(row.previous_close),
    volume: Number(row.volume),
    bid: Number(row.bid ?? ltp - spread),
    ask: Number(row.ask ?? ltp + spread),
    timestamp: new Date(row.updated_at).getTime(),
    oi:  row.oi  != null ? Number(row.oi)  : undefined,
    iv:  row.iv  != null ? Number(row.iv)  : undefined,
    atp: row.atp != null ? Number(row.atp) : undefined,
    tbq: row.tbq != null ? Number(row.tbq) : undefined,
    tsq: row.tsq != null ? Number(row.tsq) : undefined,
    greeks: parseJsonField<TickData['greeks']>(row.greeks as string | null),
    marketDepth: parseJsonField<TickData['marketDepth']>(row.market_depth as string | null),
  };
}

function rowToIndex(row: MarketLiveRow): IndexData {
  return {
    symbol: row.ticker,
    value: Number(row.current_price),
    change: Number(row.change_amount),
    pct: Number(row.change_percent),
    open: Number(row.open_price),
    high: Number(row.high_price),
    low: Number(row.low_price),
    prevClose: Number(row.previous_close),
  };
}

// ─── Context ──────────────────────────────────────────────────────────────────

const MarketDataContext = createContext<MarketDataContextValue>({
  ticks: {},
  indices: {},
  isConnected: false,
  isLive: false,
  marketSession: 'CLOSED',
  subscribe: () => {},
  unsubscribe: () => {},
  tickReceivedAt: {},
});

export function MarketDataProvider({ children }: { children: React.ReactNode }) {
  // Use stable seed (no Math.random/Date.now) so SSR and first client render match
  const [ticks, setTicks]       = useState<Record<string, TickData>>(stableTickSeed);
  const [indices, setIndices]   = useState<Record<string, IndexData>>({ ...BASE_INDICES });
  const [isConnected, setIsConnected] = useState(false);
  const [isLive, setIsLive]     = useState(false);
  const [marketSession, setMarketSession] = useState<MarketSession>('CLOSED');
  const [tickReceivedAt, setTickReceivedAt] = useState<Record<string, number>>({});

  const subscribedRef = useRef<Set<string>>(new Set(Object.keys(BASE_STOCKS)));
  const intervalRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveRef       = useRef(false);

  // ── Track market session every second ────────────────────────────────────
  useEffect(() => {
    const updateSession = () => setMarketSession(getMarketSession(new Date()));
    updateSession();
    const t = setInterval(updateSession, 1000);
    return () => clearInterval(t);
  }, []);

  // ── Load initial data from Supabase market_live_data ─────────────────────
  useEffect(() => {
    const supabase = createClient();

    const loadInitialData = async () => {
      const { data, error } = await supabase
        .from('market_live_data')
        .select('*');

      if (error || !data || data.length === 0) return;

      const newTicks: Record<string, TickData> = { ...stableTickSeed() };
      const newIndices: Record<string, IndexData> = { ...BASE_INDICES };

      for (const row of data as MarketLiveRow[]) {
        if (row.is_index) {
          newIndices[row.ticker] = rowToIndex(row);
        } else {
          newTicks[row.ticker] = rowToTick(row);
        }
      }

      setTicks(newTicks);
      setIndices(newIndices);
    };

    loadInitialData();
  }, []);

  // ── Subscribe to Supabase Realtime postgres_changes ───────────────────────
  useEffect(() => {
    const supabase = createClient();

    const channel = supabase
      .channel('market-live-data-feed')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_live_data',
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as MarketLiveRow;
          if (!row?.ticker) return;

          // ── Latency: stamp tick arrival time ─────────────────────────────
          const receivedAt = markTickReceived(row.ticker);
          setTickReceivedAt((prev) => ({ ...prev, [row.ticker]: receivedAt }));

          if (!liveRef.current) {
            // First live tick received — switch off simulation
            liveRef.current = true;
            setIsLive(true);
            setIsConnected(true);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }

          if (row.is_index) {
            setIndices((prev) => ({
              ...prev,
              [row.ticker]: rowToIndex(row),
            }));
          } else {
            setTicks((prev) => ({
              ...prev,
              [row.ticker]: rowToTick(row),
            }));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // ── Simulation fallback (runs until first live tick arrives) ─────────────
  useEffect(() => {
    const connectTimer = setTimeout(() => {
      if (!liveRef.current) {
        setIsConnected(true);
      }
    }, 400);

    intervalRef.current = setInterval(() => {
      if (liveRef.current) return; // stop if live data arrived

      const now = new Date();

      // After market close (15:30 IST) — show last close prices with day change preserved
      if (isAfterMarketClose(now)) {
        setIndices((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(next)) {
            const idx = next[key];
            // Freeze value at prevClose but keep the day's change/pct from BASE_INDICES
            const base = BASE_INDICES[key];
            if (idx.value !== idx.prevClose) {
              next[key] = {
                ...idx,
                value: idx.prevClose,
                // Preserve day change from base seed so % is always visible
                change: base ? base.change : idx.change,
                pct: base ? base.pct : idx.pct,
                high: idx.high,
                low: idx.low,
              };
            }
          }
          return next;
        });
        return;
      }

      // Pre-market (9:00–9:07): show indices at prevClose (indicative) but keep day change
      const session = getMarketSession(now);
      if (session === 'PRE_OPEN') {
        setIndices((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(next)) {
            const idx = next[key];
            const base = BASE_INDICES[key];
            next[key] = {
              ...idx,
              value: idx.prevClose,
              // Show previous day's change/pct so the % column is never blank
              change: base ? base.change : idx.change,
              pct: base ? base.pct : idx.pct,
            };
          }
          return next;
        });
        return;
      }

      // Live market hours — simulate ticks with latency marks
      const simReceivedAt: Record<string, number> = {};
      setTicks((prev) => {
        const next = { ...prev };
        for (const sym of subscribedRef.current) {
          if (next[sym]) {
            simReceivedAt[sym] = markTickReceived(sym);
            next[sym] = simulateTick(next[sym]);
          }
        }
        return next;
      });
      if (Object.keys(simReceivedAt).length > 0) {
        setTickReceivedAt((prev) => ({ ...prev, ...simReceivedAt }));
      }
      setIndices((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = simulateIndex(next[key]);
        }
        return next;
      });
    }, 500);

    return () => {
      clearTimeout(connectTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const subscribe = useCallback((symbols: string[]) => {
    symbols.forEach((s) => subscribedRef.current.add(s));
  }, []);

  const unsubscribe = useCallback((symbols: string[]) => {
    symbols.forEach((s) => subscribedRef.current.delete(s));
  }, []);

  return (
    <MarketDataContext.Provider value={{ ticks, indices, isConnected, isLive, marketSession, subscribe, unsubscribe, tickReceivedAt }}>
      {children}
    </MarketDataContext.Provider>
  );
}

export function useMarketData() {
  return useContext(MarketDataContext);
}

export function useTick(symbol: string): TickData | null {
  const { ticks } = useMarketData();
  return ticks[symbol] ?? null;
}

export function useIndex(symbol: string): IndexData | null {
  const { indices } = useMarketData();
  return indices[symbol] ?? null;
}

export function useTickDepth(symbol: string): Array<{ bidQ: number; bidP: number; askQ: number; askP: number; }> | null {
  const { ticks } = useMarketData();
  const tick = ticks[symbol];
  return tick?.marketDepth ?? null;
}

export function useTickGreeks(symbol: string): { delta?: number; theta?: number; gamma?: number; vega?: number; rho?: number; } | null {
  const { ticks } = useMarketData();
  const tick = ticks[symbol];
  return tick?.greeks ?? null;
}
